import { QueryClient, onlineManager } from "@tanstack/react-query";

export const ohQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // prevent refetch on window / tab refocus
      refetchOnWindowFocus: false,
      // prevent retry if a request fail
      retry: false,
      // remove query data from catch once a hook is unmount
      // keep react-query behavior iso to hb current expected behavior
      cacheTime: 0,
    },
  },
});

// ------------------------------------------
// ---- Global pause on network requests ----
// ------------------------------------------
// this is used to pause all network requests when a rate limit is reached
// it will automatically resume requests after the specified delay in the "retry-after" header
// - when detecting a rate limit error, it will :
//   - set the online status to false
//   - stop pending queries
//   - prevent queries from fetching automatically
//   - after the given delay (retrieved from the "retry-after" or "x-retry-in" header), it will:
//     - set the online status to true
//     - restart queries related to the rate limit error
//     - clear the global pause timer
//     - allow unstarted queries to be launched

let globalPauseTimer: NodeJS.Timeout | null = null;
let defaultOptions = ohQueryClient.getDefaultOptions();

ohQueryClient.getQueryCache().subscribe(event => {
  // if the event is not a react-query error, do nothing
  if (event.type !== "updated" || event.action.type !== "error") return;

  // if error does not have "headers" or "retry-after" property, do nothing
  if (
    !event.action.error ||
    !event.action.error.headers ||
    (!event.action.error.headers.has("retry-after") &&
      !event.action.error.headers.has("x-retry-in"))
  )
    return;

  // if globalPauseTimer is already set, kill it (to recreate it with the new delay)
  if (globalPauseTimer) {
    clearTimeout(globalPauseTimer);
    globalPauseTimer = null;
  }

  // extract retry data from header
  const retryAfter = event.action.error.headers.get("retry-after");
  const xRetryIn = event.action.error.headers.get("x-retry-in");

  // compute delay and prioritize x-retry-in (because retry-after is a rounded value)
  let delay = 1000;

  if (xRetryIn) {
    const multiplier = xRetryIn.endsWith("ms") ? 1 : 1000;
    delay = multiplier * parseInt(xRetryIn);
  } else if (retryAfter) {
    delay = 1000 * parseInt(retryAfter);
  }

  // set online status to false to act as if the user is offline
  onlineManager.setOnline(false);
  // cancel all queries to prevent any new requests
  ohQueryClient.cancelQueries();
  // ensure that when going back online, queries will not refetch immediately (to avoid useless requests and stress on the server)
  ohQueryClient.setDefaultOptions({
    queries: {
      ...defaultOptions.queries,
      refetchOnReconnect: q => {
        // check if query where concerned by the rate limit
        const err: { headers?: Headers } = q.state.error as any;
        // if not, prevent refetching
        if (
          !err ||
          !err.headers ||
          (!err.headers.has("retry-after") && !err.headers.has("x-retry-in"))
        )
          return false;
        // if yes, do not refetch automatically
        return true;
      },
    },
  });
  // set a timer to resume requests after the specified delay
  globalPauseTimer = setTimeout(() => {
    // set online status to true to act as if the user is back online
    onlineManager.setOnline(true);
    globalPauseTimer = null;
    // reset the default options
    ohQueryClient.setDefaultOptions(defaultOptions);
  }, delay);
});
